import { startTransition, useCallback, useEffect, useOptimistic, useRef, useState } from 'react';
import { interactiveServiceFetch } from '../../services/interactiveServiceFetch';
import { ARTIFACT_TYPES, CHAT_EVENTS, EVENT_TYPES, MESSAGE_ROLES, SESSION_TYPES, trackChatEvent } from '../constants';

const isSystemError = block => block.type === ARTIFACT_TYPES.ERROR && block.error_type === EVENT_TYPES.ERROR;

const sessionTypeOf = (sessions, sessionId) => sessions.find(s => s.session_id === sessionId)?.session_type;

// A Slack mention creates and advances a session with no browser involved, and the listing is the
// only place its run is announced - polling it is what lets `useChat` attach that run's stream.
const SESSION_POLL_INTERVAL_MS = 10000;

const useChatSessions = ({
    api,
    sessionType = SESSION_TYPES.CHAT,
    onSessionChange = () => {},
    onSessionRemoved = () => {},
    loadSessionsOnMount = true,
}) => {
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);
    const [sessionsLoadError, setSessionsLoadError] = useState(false);
    const [loadingSessionId, setLoadingSessionId] = useState(null);
    const [sessionDeletedNotice, setSessionDeletedNotice] = useState(null);
    const [sessionDeleteError, setSessionDeleteError] = useState(null);
    const [sessionLoadError, setSessionLoadError] = useState(null);
    // The open conversation's own type, kept beside its id: the sidebar list is capped and
    // refetched, so a session opened by URL can drop out of it while still being open.
    const [loadedSession, setLoadedSession] = useState(null);

    const [optimisticSessions, updateOptimisticSessions] = useOptimistic(
        sessions,
        (currentSessions, { sessionId, title }) =>
            currentSessions.map(session => (session.session_id === sessionId ? { ...session, title } : session))
    );

    const sessionsRef = useRef(sessions);
    // Sessions this tab put in the sidebar before the backend had a row for them.
    const localSessionIdsRef = useRef(new Set());
    const sessionsRequestRef = useRef(0);

    useEffect(() => {
        sessionsRef.current = sessions;
    }, [sessions]);

    const requestSessions = useCallback(async () => {
        sessionsRequestRef.current += 1;
        const request = sessionsRequestRef.current;
        const response = await interactiveServiceFetch(`${api}/sessions?session_type=${sessionType}`);

        if (!response.ok) {
            throw new Error('Failed to fetch sessions');
        }

        const data = await response.json();
        // null once a newer request has overtaken this one: an older listing applied last drops
        // the run it never carried, and the poll interval is shorter than a slow response.
        if (request !== sessionsRequestRef.current) return null;
        return data.sessions || [];
    }, [api, sessionType]);

    // The run creates a new conversation's row a moment after the composer does, so either fetch
    // can land while the listing still has no idea it exists.
    const applySessions = useCallback(incoming => {
        const listed = new Set(incoming.map(session => session.session_id));
        setSessions(prev => {
            const unlisted = prev.filter(
                session => localSessionIdsRef.current.has(session.session_id) && !listed.has(session.session_id)
            );
            return unlisted.length ? [...unlisted, ...incoming] : incoming;
        });
        listed.forEach(sessionId => localSessionIdsRef.current.delete(sessionId));
    }, []);

    const fetchSessions = useCallback(async () => {
        setIsLoadingSessions(true);
        setSessionsLoadError(false);
        try {
            const incoming = await requestSessions();
            if (incoming) applySessions(incoming);
        } catch (err) {
            console.error('Failed to fetch sessions:', err);
            setSessionsLoadError(true);
        } finally {
            setIsLoadingSessions(false);
        }
    }, [applySessions, requestSessions]);

    // Quiet by design: no loading or error state.
    const refreshSessions = useCallback(async () => {
        try {
            const incoming = await requestSessions();
            if (incoming) applySessions(incoming);
        } catch (err) {
            console.warn('Failed to refresh sessions:', err);
        }
    }, [applySessions, requestSessions]);

    useEffect(() => {
        if (loadSessionsOnMount) fetchSessions();
    }, [fetchSessions, loadSessionsOnMount]);

    // Only the chat listing merges the Slack conversations, and only once auth is ready.
    const pollSessions = loadSessionsOnMount && sessionType === SESSION_TYPES.CHAT;

    useEffect(() => {
        if (!pollSessions) return undefined;
        const refreshWhenVisible = () => {
            if (!document.hidden) refreshSessions();
        };
        const timer = setInterval(refreshWhenVisible, SESSION_POLL_INTERVAL_MS);
        document.addEventListener('visibilitychange', refreshWhenVisible);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', refreshWhenVisible);
        };
    }, [pollSessions, refreshSessions]);

    const loadSession = useCallback(
        async sessionId => {
            if (!sessionId) return;

            setLoadingSessionId(sessionId);
            try {
                const response = await interactiveServiceFetch(
                    `${api}/sessions/${sessionId}?session_type=${sessionType}`
                );

                if (response.status === 404) {
                    setCurrentSessionId(null);
                    setLoadedSession(null);
                    onSessionChange(null, true);
                    onSessionRemoved(sessionId);
                    setSessions(prev => prev.filter(s => s.session_id !== sessionId));
                    setSessionDeletedNotice(prev => ({
                        sessionId,
                        nonce: (prev?.nonce ?? 0) + 1,
                    }));
                    return null;
                }

                if (!response.ok) {
                    throw new Error('Failed to load session');
                }

                const data = await response.json();
                setLoadedSession({ sessionId, sessionType: data.session_type || sessionType });

                setSessions(prev => {
                    const summary = {
                        session_id: data.session_id,
                        title: data.title,
                        session_type: data.session_type || sessionType,
                        active_run_id: data.active_run_id || null,
                    };
                    const existingIndex = prev.findIndex(session => session.session_id === sessionId);
                    if (existingIndex === -1) return [summary, ...prev];
                    return prev.map(session =>
                        session.session_id === sessionId ? { ...session, ...summary } : session
                    );
                });

                // Convert session messages to chat message format
                const loadedMessages = data.messages.map((msg, index) => {
                    return {
                        id: msg.id || `${msg.role}-${sessionId}-${index}`,
                        runId: msg.run_id || null,
                        role: msg.role,
                        content: msg.content,
                        contextFiles: msg.context_files || [],
                        createdAt: new Date(msg.created_at * 1000), // Convert Unix timestamp to Date
                        isThinking: false,
                        isStreaming: false,
                        steps: [],
                        isHistorical: true, // Flag to indicate this is from history
                    };
                });

                // Check if the last assistant message ended with a report_error block
                const lastAssistantMsg = loadedMessages.findLast(({ role }) => role === MESSAGE_ROLES.ASSISTANT);
                const sessionError = lastAssistantMsg?.content?.findLast?.(isSystemError)?.data;

                if (sessionError) {
                    const lastErrorIdx = lastAssistantMsg.content.findLastIndex(isSystemError);
                    lastAssistantMsg.content = lastAssistantMsg.content.filter((_, i) => i !== lastErrorIdx);
                }

                setCurrentSessionId(sessionId);
                onSessionChange(sessionId, false, loadedMessages, sessionError, data.active_run_id || null);

                return loadedMessages;
            } catch (err) {
                console.error('Failed to load session:', err);
                setSessionLoadError(prev => ({
                    sessionId,
                    nonce: (prev?.nonce ?? 0) + 1,
                }));
                return null;
            } finally {
                setLoadingSessionId(null);
            }
        },
        [api, sessionType, onSessionChange, onSessionRemoved]
    );

    const deleteSession = useCallback(
        async sessionId => {
            if (!sessionId) return false;

            const index = sessionsRef.current.findIndex(s => s.session_id === sessionId);
            if (index === -1) return false;
            const sessionToRestore = sessionsRef.current[index];
            const wasCurrent = sessionId === currentSessionId;

            setSessions(prev => prev.filter(s => s.session_id !== sessionId));

            if (wasCurrent) {
                setCurrentSessionId(null);
                onSessionChange(null, true);
            }

            try {
                const response = await interactiveServiceFetch(`${api}/sessions/${sessionId}`, {
                    method: 'DELETE',
                });

                if (!response.ok) {
                    throw new Error('Failed to delete session');
                }

                onSessionRemoved(sessionId);
                trackChatEvent(CHAT_EVENTS.DELETE_CHAT, { sessionId });

                return true;
            } catch (err) {
                console.error('Failed to delete session:', err);
                setSessions(prev => {
                    if (prev.some(s => s.session_id === sessionId)) return prev;
                    const next = [...prev];
                    next.splice(Math.min(index, next.length), 0, sessionToRestore);
                    return next;
                });

                if (wasCurrent) setCurrentSessionId(current => (current === null ? sessionId : current));

                setSessionDeleteError(prev => ({
                    sessionId,
                    nonce: (prev?.nonce ?? 0) + 1,
                }));
                return false;
            }
        },
        [api, currentSessionId, onSessionChange, onSessionRemoved]
    );

    const updateSessionTitle = useCallback(
        async (sessionId, title) => {
            if (!sessionId || !title) return false;

            startTransition(() => {
                updateOptimisticSessions({ sessionId, title });
            });

            try {
                const response = await interactiveServiceFetch(`${api}/sessions/${sessionId}/title`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ title }),
                });

                if (!response.ok) {
                    throw new Error('Failed to update session title');
                }

                setSessions(prevSessions =>
                    prevSessions.map(session => (session.session_id === sessionId ? { ...session, title } : session))
                );
                trackChatEvent(CHAT_EVENTS.RENAME_CHAT, { sessionId, titleLength: title.length });

                return true;
            } catch (err) {
                console.error('Failed to update session title:', err);
                return false;
            }
        },
        [api, updateOptimisticSessions]
    );

    const upsertSession = useCallback(
        (sessionId, title) => {
            setSessions(prev => {
                const exists = prev.some(session => session.session_id === sessionId);
                if (exists)
                    return prev.map(session => (session.session_id === sessionId ? { ...session, title } : session));

                localSessionIdsRef.current.add(sessionId);
                return [{ session_id: sessionId, title, session_type: sessionType, message_count: 0 }, ...prev];
            });
        },
        [sessionType]
    );

    const currentSessionType =
        (loadedSession?.sessionId === currentSessionId && loadedSession.sessionType) ||
        (currentSessionId && sessionTypeOf(optimisticSessions, currentSessionId)) ||
        sessionType;

    return {
        currentSessionId,
        currentSessionType,
        setCurrentSessionId,
        sessions: optimisticSessions,
        isLoadingSessions,
        sessionsLoadError,
        loadingSessionId,
        sessionDeletedNotice,
        sessionDeleteError,
        sessionLoadError,
        fetchSessions,
        upsertSession,
        loadSession,
        deleteSession,
        updateSessionTitle,
    };
};

export default useChatSessions;
